/**
 * Puerto y base URL de la API local que levanta la extensión (Electron).
 *
 * Root guarda el puerto al arrancar. Antes cada repositorio leía la clave por su
 * cuenta y varios la congelaban en el constructor: el singleton de workspace se
 * construía al evaluar el módulo, antes de que Root resolviera nada, y quedaba
 * apuntando a `http://localhost:null` para toda la sesión. En un reporte de
 * soporte real eso degradó el tipo de usuario al valor por defecto y armó la
 * escena inicial equivocada.
 *
 * Reglas:
 * - La base se resuelve EN CADA LLAMADA. Nunca cachearla en un campo.
 * - Nadie lee la clave cruda fuera de este archivo (hay un test que lo vigila).
 * - Un valor que no sea un puerto válido se trata como ausente: interpolarlo en
 *   la URL podría redirigir el fetch a otro host.
 */
export const LOCAL_API_PORT_KEY = "@Eriol/system/api.port";
export const DEFAULT_LOCAL_API_PORT = 55500;

const isValidPort = (value: number): boolean =>
  Number.isInteger(value) && value >= 1 && value <= 65535;

/**
 * Puerto guardado por Root, o null si no hay ninguno (browser sin Electron,
 * un worker sin localStorage, o la carrera de arranque). Para gatear "¿hay
 * server local?"; para armar URLs usar `localApiBase()`.
 */
export function storedLocalApiPort(): number | null {
  if (typeof localStorage === "undefined") return null;

  const raw = localStorage.getItem(LOCAL_API_PORT_KEY);

  // Sólo dígitos decimales: `Number()` aceptaría "0x10" o "1e3", que no son
  // la forma en que la extensión escribe un puerto.
  if (!raw || !/^\d{1,5}$/.test(raw)) return null;

  const port = Number(raw);

  return isValidPort(port) ? port : null;
}

/** Puerto a usar siempre: el guardado, o el que la extensión asigna por defecto. */
export function localApiPort(): number {
  return storedLocalApiPort() ?? DEFAULT_LOCAL_API_PORT;
}

/** Base URL de la API local, resuelta en el momento. */
export function localApiBase(): string {
  return `http://localhost:${localApiPort()}`;
}

/** Guarda el puerto que reporta la extensión. Devuelve false si no es un puerto válido. */
export function storeLocalApiPort(port: number): boolean {
  if (!isValidPort(port)) return false;

  localStorage.setItem(LOCAL_API_PORT_KEY, String(port));

  return true;
}

/** Forma mínima de lo que contesta el preload en `electronAPI.getPorts()`. */
export interface DesktopPortsResult {
  success?: boolean;
  ports?: { api?: number | string };
}

/**
 * Toma el puerto que reporta el desktop y lo deja guardado. Devuelve el puerto
 * adoptado, o null si la respuesta no traía uno válido.
 *
 * El preload contesta con el puerto por defecto y `success: false` cuando el
 * IPC falla. Ese puerto sirve igual: exigir `success` dejaba la clave vacía
 * para toda la sesión, que es la condición que armaba `localhost:null`.
 */
export function adoptDesktopPorts(
  result: DesktopPortsResult | null | undefined,
): number | null {
  const port = Number(result?.ports?.api);

  if (!storeLocalApiPort(port)) return null;

  if (!result?.success) {
    console.warn(`[ports] getPorts falló; se usa el puerto de rescate ${port}`);
  }

  return port;
}
